import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ITransitionJourneyAgentInsights, ITransitionJourneyNodeData, ITransitionJourneyNodeDataItem, ITransitionJourneyProject, ITransitionJourneyTreeNode } from '../../interfaces/AppInterfaces';
import { transitionManagerPersonaNodes } from '../../constants/TransitionJourneyPersonas';
import { TRANSITION_JOURNEY_NODE_TYPE } from '../../constants/appConstants';
import _ from 'lodash';
import { ILevelItem } from '../../components/hierarchicalSelectComponent/HierarchicalSelectComponent';
import { ValidRole } from '../../interfaces/serviceInterfaces';
import { SearchMetadataResponse } from '../../helpers/searchMetadataHelper';
import { sassNull } from 'sass';

export interface TransitionJourneyState {
    projects: ITransitionJourneyProject[],
    projectsData?: ITransitionJourneyNodeData,
    appliedFilters: Record<string, any>,
    showAssessment: boolean;
    selectedPersona?: string;
    dashboard?: any,
    agentInsights: ITransitionJourneyAgentInsights;
    visitedProjects: {
        [key: string]: {
            data: ITransitionJourneyTreeNode[],
            expandedNodes: string[],
            selectedNode?: ITransitionJourneyTreeNode;
            workLogs?: ITransitionJourneyNodeDataItem[];
            selectedLevel?: string;
            selectedLevelValue?: string | string[];
        }
    };
    refreshView: boolean;
    levels: ILevelItem[];
    allRoles: ValidRole[];
    playbackAssessmentAllowedRoles: any[];
    searchMetadata?: SearchMetadataResponse;

};

const initialState: TransitionJourneyState = {
    projects: [],
    projectsData: undefined,
    appliedFilters: {},
    agentInsights: {
        activeAgentTasks: [],
        workflowLogs: [],
    },
    visitedProjects: {

    },
    showAssessment: false,
    refreshView: true,
    levels: [],
    allRoles: [],
    playbackAssessmentAllowedRoles: [],
    searchMetadata: undefined,
    selectedPersona: undefined

}

const updateProjectData = (data: ITransitionJourneyTreeNode[], updatedNode: ITransitionJourneyTreeNode): ITransitionJourneyTreeNode[] => {
    const updatedProjectData: ITransitionJourneyTreeNode[] = _.cloneDeep(data);
    updatedProjectData.forEach((node: ITransitionJourneyTreeNode) => {
        node.children?.forEach((child: ITransitionJourneyTreeNode, index: number) => {
            if (child.nodeType === updatedNode.nodeType) {
                if (node.children) {
                    node.children[index] = updatedNode;
                }
            }
        });
    });
    return updatedProjectData;
}

const transitionJourneySlice = createSlice({
    name: 'transitionJourney',
    initialState,
    reducers: {
        setProjectInitialState: (state, action: PayloadAction<{ projectId: string, data: ITransitionJourneyTreeNode[] }>) => {
            const { projectId, data } = action.payload;
            state.visitedProjects[projectId] = {
                data: data,
                expandedNodes: [],
                selectedNode: undefined
            }
        },
        setSelectedNode: (state, action: PayloadAction<{ projectId: string, node: ITransitionJourneyTreeNode }>) => {
            const { projectId, node } = action.payload;
            if (state.visitedProjects[projectId]) {
                state.visitedProjects[projectId] = {
                    ...state.visitedProjects[projectId],
                    selectedNode: node
                }
            }
        },
        setExpandedNode: (state, action: PayloadAction<{ projectId: string, nodeId: string }>) => {
            const { projectId, nodeId } = action.payload;
            if (state.visitedProjects[projectId]) {
                state.visitedProjects[projectId] = {
                    ...state.visitedProjects[projectId],
                    expandedNodes: [...state.visitedProjects[projectId]['expandedNodes'], nodeId]
                }
            }
        },
        removeExpandedNode: (state, action: PayloadAction<{ projectId: string, nodeId: string }>) => {
            const { projectId, nodeId } = action.payload;
            if (state.visitedProjects[projectId]) {
                const updatedExpandedNodes = state.visitedProjects[projectId]['expandedNodes'].filter((value) => value !== nodeId);
                state.visitedProjects[projectId] = {
                    ...state.visitedProjects[projectId],
                    expandedNodes: updatedExpandedNodes
                }
            }
        },
        setTransitionJourneyProjects: (state, action: PayloadAction<{ projects: ITransitionJourneyProject[] }>) => {
            state.projects = action.payload.projects;
        },
        setTransitionJourneyProjectsData: (state, action: PayloadAction<{ projectsData: ITransitionJourneyNodeData }>) => {
            state.projectsData = action.payload.projectsData;
            state.projects = action.payload.projectsData.data as ITransitionJourneyProject[];
        },
        addTransitionJourneyProject: (state, action: PayloadAction<{ project: ITransitionJourneyProject }>) => {
            state.projects = [...state.projects, action.payload.project];
        },
        setTransitionJourneyAgentInsights: (state, action: PayloadAction<{ agentInsights: ITransitionJourneyAgentInsights }>) => {
            state.agentInsights = action.payload.agentInsights;
        },
        setTransitionJourneyNodeData: (state, action: PayloadAction<{ projectId: string, node: ITransitionJourneyTreeNode }>) => {
            const { projectId, node } = action.payload;
            if (state.visitedProjects[projectId]) {
                state.visitedProjects[projectId] = {
                    ...state.visitedProjects[projectId],
                    data: updateProjectData(state.visitedProjects[projectId].data, node)
                }
            }
        },
        updateNodeActivationStatus: (state, action: PayloadAction<{ projectId: string, activate: boolean }>) => {
            const { projectId, activate } = action.payload;
            const project = state.visitedProjects[projectId];
            if (project) {
                project.data.forEach(function traverse(node) {
                    // if (node.nodeType !== TRANSITION_JOURNEY_NODE_TYPE.CONNECTORS && node.nodeType !== TRANSITION_JOURNEY_NODE_TYPE.PROJECT_DETAILS) {
                    //     node.activated = activate;
                    // }
                    node.activated = activate;
                    node.children?.forEach(traverse);
                });
            }
        },
        showAssessment: (state, action: PayloadAction<{ show: boolean }>) => {
            state.showAssessment = action.payload.show;
        },
        setSelectedLevelAndValue: (state, action: PayloadAction<{ projectId: string, level: string | null, levelValue: string | string[] | null }>) => {
            const { projectId, level, levelValue } = action.payload;
            if (state.visitedProjects[projectId]) {
                state.visitedProjects[projectId] = {
                    ...state.visitedProjects[projectId],
                    selectedLevel: level || undefined,
                    selectedLevelValue: levelValue || undefined
                }
            }
        },
        refreshView: (state, action: PayloadAction<{ refresh: boolean }>) => {
            state.refreshView = action.payload.refresh;
        },
        updateLevels: (state, action: PayloadAction<{ levels: ILevelItem[] }>) => {
            state.levels = action.payload.levels;
        },
        setAllRoles: (state, action: PayloadAction<{ allRoles: ValidRole[], playbackAssessmentAllowedRoles: any[] }>) => {
            state.allRoles = action.payload.allRoles;
            state.playbackAssessmentAllowedRoles = action.payload.playbackAssessmentAllowedRoles
        },
        setSearchMetadata: (state, action: PayloadAction<{ searchMetadata: SearchMetadataResponse }>) => {
            state.searchMetadata = action.payload.searchMetadata;
        },
        setAppliedFilters: (state, action: PayloadAction<{ appliedFilters: Record<string, any> }>) => {
            state.appliedFilters = action.payload.appliedFilters;
        },
        setSelectedPersona: (state, action: PayloadAction<{ persona: string }>) => {
            state.selectedPersona = action.payload.persona;
        },
        clearVisitedProjects: (state) => {
            state.visitedProjects = {};
        }
    },
});

export const { setSelectedNode, setProjectInitialState, setExpandedNode, removeExpandedNode, setTransitionJourneyProjects, setTransitionJourneyProjectsData, addTransitionJourneyProject, setTransitionJourneyAgentInsights, setTransitionJourneyNodeData, updateNodeActivationStatus, showAssessment, setSelectedLevelAndValue, refreshView, updateLevels, setAllRoles, setSearchMetadata, setAppliedFilters, setSelectedPersona, clearVisitedProjects } = transitionJourneySlice.actions;
export default transitionJourneySlice.reducer;